import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  ActiveRateResponse,
  CurrencyRateRow,
  CustomerStatusEventType,
  ListStatusEventTemplatesResponse,
  ListTemplatesResponse,
  PreviewTemplateResponse,
  RateHistoryResponse,
  SendReminderResponse,
  TemplateKind,
  UpsertNotificationSettingsResponse,
  UpsertStatusEventTemplateResponse,
  UpsertTemplateResponse,
  UpsertUtilitySettingsResponse,
} from "./types";

const base = `${config.apiUrl}/cf-message`;

export const cfMessageApi = {
  listTemplates(pageId: string): Promise<ListTemplatesResponse> {
    const url = new URL(`${base}/templates`);
    url.searchParams.set("pageId", pageId);
    return fetcher.get(url.toString());
  },

  upsertTemplate(
    kind: TemplateKind,
    body: { pageId: string; content: string; isActive?: boolean },
  ): Promise<UpsertTemplateResponse> {
    return fetcher.put(`${base}/templates/${kind}`, body);
  },

  resetTemplate(
    kind: TemplateKind,
    body: { pageId: string },
  ): Promise<UpsertTemplateResponse> {
    return fetcher.post(`${base}/templates/${kind}/reset`, body);
  },

  upsertNotificationSettings(body: {
    pageId: string;
    enabledStatusEvents: CustomerStatusEventType[];
  }): Promise<UpsertNotificationSettingsResponse> {
    return fetcher.put(`${base}/notification-settings`, body);
  },

  upsertUtilitySettings(body: {
    pageId: string;
    paymentReminderTemplateName?: string | null;
    orderStatusTemplateName?: string | null;
    deliveryStatusTemplateName?: string | null;
    deliveryArtifactTemplateName?: string | null;
    languageCode?: string;
    templatesReady: boolean;
  }): Promise<UpsertUtilitySettingsResponse> {
    return fetcher.put(`${base}/utility-settings`, body);
  },

  listStatusEventTemplates(
    pageId: string,
  ): Promise<ListStatusEventTemplatesResponse> {
    const url = new URL(`${base}/status-event-templates`);
    url.searchParams.set("pageId", pageId);
    return fetcher.get(url.toString());
  },

  upsertStatusEventTemplate(
    eventType: CustomerStatusEventType,
    body: { pageId: string; content: string },
  ): Promise<UpsertStatusEventTemplateResponse> {
    return fetcher.put(`${base}/status-event-templates/${eventType}`, body);
  },

  preview(body: {
    kind: TemplateKind;
    content?: string;
  }): Promise<PreviewTemplateResponse> {
    return fetcher.post(`${base}/preview`, body);
  },

  sendReminder(orderId: string): Promise<SendReminderResponse> {
    return fetcher.post(`${base}/send-reminder`, { orderId });
  },

  activeRate(): Promise<ActiveRateResponse> {
    return fetcher.get(`${base}/rates/active`);
  },

  rateHistory(limit = 50): Promise<RateHistoryResponse> {
    const url = new URL(`${base}/rates/history`);
    url.searchParams.set("limit", String(limit));
    return fetcher.get(url.toString());
  },

  createRate(input: {
    rateKipToBaht: number;
    rateKipToUsd: number;
    notes?: string | null;
  }): Promise<{ rate: CurrencyRateRow }> {
    return fetcher.post(`${base}/rates`, input);
  },
};
